/** IST calendar helpers for analytics range controls. */

function pad(n) {
  return String(n).padStart(2, '0');
}

/** Current calendar date parts in Asia/Kolkata. */
export function istParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type) => parts.find((p) => p.type === type)?.value;
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
  };
}

export function toYmd({ year, month, day }) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

export function todayYmd() {
  return toYmd(istParts());
}

export function addDaysYmd(ymd, days) {
  const [y, m, d] = ymd.split('-').map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  utc.setUTCDate(utc.getUTCDate() + days);
  return `${utc.getUTCFullYear()}-${pad(utc.getUTCMonth() + 1)}-${pad(utc.getUTCDate())}`;
}

export function daysInclusive(from, to) {
  if (!from || !to) return 0;
  const a = new Date(`${from}T00:00:00Z`);
  const b = new Date(`${to}T00:00:00Z`);
  return Math.floor((b - a) / 86400000) + 1;
}

export function formatRangeLabel(from, to) {
  if (!from || !to) return '—';
  const fmt = (ymd) => {
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };
  return `${fmt(from)} — ${fmt(to)}`;
}

export function formatShortRange(from, to) {
  if (!from || !to) return '—';
  const fmt = (ymd) => {
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
  };
  return `${fmt(from)} – ${fmt(to)}`;
}

export function formatAsOf(iso) {
  if (!iso) {
    const p = istParts();
    const h = p.hour % 12 || 12;
    const ampm = p.hour >= 12 ? 'PM' : 'AM';
    return `as of ${h}:${pad(p.minute)} ${ampm}`;
  }
  const d = new Date(iso);
  return `as of ${d.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

export const PERIOD_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: 'this_month', label: 'This month' },
  { key: 'custom', label: 'Custom' },
];

export const CHANNELS = [
  { key: 'all', label: 'All' },
  { key: 'pos', label: 'POS' },
  { key: 'delivery', label: 'Delivery' },
];

export function channelLabel(channel) {
  if (channel === 'pos') return 'POS';
  if (channel === 'delivery') return 'Delivery';
  return 'All channels';
}

/** Resolve preset + optional custom dates into from/to (YYYY-MM-DD, IST calendar). */
export function resolveRange(preset, customFrom, customTo) {
  const today = todayYmd();

  if (preset === 'today') {
    return { from: today, to: today, period: 'today' };
  }
  if (preset === 'yesterday') {
    const y = addDaysYmd(today, -1);
    return { from: y, to: y, period: 'yesterday' };
  }
  if (preset === '7d') {
    return { from: addDaysYmd(today, -6), to: today, period: '7d' };
  }
  if (preset === '30d') {
    return { from: addDaysYmd(today, -29), to: today, period: '30d' };
  }
  if (preset === 'this_month') {
    const p = istParts();
    return { from: toYmd({ year: p.year, month: p.month, day: 1 }), to: today, period: 'this_month' };
  }

  const from = customFrom || today;
  const to = customTo || today;
  return {
    from: from <= to ? from : to,
    to: from <= to ? to : from,
    period: 'custom',
  };
}

export function previousWindow(from, to) {
  const len = daysInclusive(from, to);
  const prevTo = addDaysYmd(from, -1);
  const prevFrom = addDaysYmd(prevTo, -(len - 1));
  return { from: prevFrom, to: prevTo };
}

/** Live widgets: inclusive days ≤ 90 OK; > 90 → FE disables + API 400 range_too_long. */
export const MAX_DASHBOARD_DAYS = 90;

/** Reports: inclusive days ≤ 366 OK; > 366 → warn + API 400 range_too_long. */
export const MAX_REPORT_DAYS = 366;

/** Hour only when range ≤ 2 inclusive IST days (else API 400 invalid_granularity). */
export function hourGranularityAllowed(from, to) {
  return daysInclusive(from, to) <= 2;
}

export function defaultGranularity(from, to) {
  const days = daysInclusive(from, to);
  if (days <= 2) return 'hour';
  if (days <= 45) return 'day';
  return 'week';
}
